import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone, Mail, FileText, Clock, MapPin, CheckCircle, 
  Send, Calendar, Users, Award, Building, Car, 
  Briefcase, ArrowRight, Download, MessageSquare, Scale,
  ClipboardList, Bell, Search, BarChart3, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from './Header';
import Footer from './Footer';
import usePageSeo from '../hooks/usePageSeo';

// ── Validators ────────────────────────────────────────────────────────────────

const validateName = (value, fieldLabel) => {
  if (!value.trim()) return `${fieldLabel} is required.`;
  if (value.trim().length < 2) return `${fieldLabel} must be at least 2 characters.`;
  if (value.trim().length > 50) return `${fieldLabel} must be under 50 characters.`;
  if (!/^[a-zA-Z\s'\-]+$/.test(value.trim()))
    return `${fieldLabel} can only contain letters, spaces, hyphens, or apostrophes.`;
  return '';
};

const validateEmail = (value) => {
  if (!value) return ''; // optional
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()))
    return 'Please enter a valid email address.';
  return '';
};

const validateRequiredEmail = (value, fieldLabel = 'Email') => {
  if (!value.trim()) return `${fieldLabel} is required.`;
  return validateEmail(value);
};

const validateMobile = (value) => {
  if (!value.trim()) return 'Mobile number is required.';
  const digits = value.replace(/[\s\-().+]/g, '');
  if (!/^\d+$/.test(digits))
    return 'Mobile number can only contain digits, spaces, +, -, or parentheses.';
  if (digits.length < 7 || digits.length > 15)
    return 'Mobile number must be between 7 and 15 digits.';
  return '';
};

const validateDOB = (value) => {
  if (!value) return 'Date of birth is required.';
  const dob = new Date(value);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (isNaN(dob.getTime())) return 'Please enter a valid date.';
  if (dob >= today) return 'Date of birth must be in the past.';
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 120);
  if (dob < minDate) return 'Please enter a realistic date of birth.';
  return '';
};

// ─────────────────────────────────────────────────────────────────────────────

const initialFormData = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  email: '',
  mobile: '',
  referrerName: '',
  referrerEmail: '',
  notes: ''
};

const initialErrors = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  email: '',
  mobile: '',
  referrerName: '',
  referrerEmail: ''
};

const ForAttorneys = () => {
  usePageSeo({
    title: "Attorney Referrals for Personal Injury Care | ATL Regen",
    description: "ATL Regen partners with personal injury attorneys in Atlanta, providing PRP therapy, expert documentation, and non-surgical treatment.",
    canonical: "https://www.atlregen.com/for-attorneys",
    ogTitle: "ATL Regen for Attorneys | Personal Injury Patient Referrals",
  });

  const referralSource = 'Attorney';
  const [showReferralForm, setShowReferralForm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const [captchaError, setCaptchaError] = useState('');
  const [captchaRenderKey, setCaptchaRenderKey] = useState(0);
  const [captchaReady, setCaptchaReady] = useState(false);
  const recaptchaRef = useRef(null);

  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000')
    .trim()
    .replace(/^['\"]|['\"]$/g, '');
  const siteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '')
    .trim()
    .replace(/^['\"]|['\"]$/g, '');

  // ── Reset on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (showReferralForm) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setTouched({});
      setSubmitSuccess(false);
      setCaptchaToken('');
      setCaptchaChecked(false);
      setCaptchaError('');
      setCaptchaReady(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  }, [showReferralForm]);

  useEffect(() => {
    if (!siteKey) setCaptchaError('Captcha key missing');
  }, [siteKey]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const resetCaptcha = (message) => {
    setCaptchaToken('');
    setCaptchaChecked(false);
    if (message) setCaptchaError(message);
    if (recaptchaRef.current) recaptchaRef.current.reset();
    setCaptchaRenderKey((prev) => prev + 1);
  };

  const getFieldError = (name, value) => {
    switch (name) {
      case 'firstName':    return validateName(value, 'First name');
      case 'lastName':     return validateName(value, 'Last name');
      case 'referrerName': return validateName(value, 'Referrer name');
      case 'email':        return validateEmail(value);
      case 'referrerEmail': return validateEmail(value); // optional
      case 'mobile':       return validateMobile(value);
      case 'dateOfBirth':  return validateDOB(value);
      default:             return '';
    }
  };

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: getFieldError(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: getFieldError(name, value) }));
  };

  const validateAll = () => {
    const newErrors = {
      firstName:    validateName(formData.firstName, 'First name'),
      lastName:     validateName(formData.lastName, 'Last name'),
      dateOfBirth:  validateDOB(formData.dateOfBirth),
      email:        validateEmail(formData.email),
      mobile:       validateMobile(formData.mobile),
      referrerName: validateName(formData.referrerName, 'Referrer name'),
      referrerEmail: validateEmail(formData.referrerEmail),
    };
    setErrors(newErrors);
    setTouched({
      firstName: true, lastName: true, dateOfBirth: true,
      email: true, mobile: true, referrerName: true, referrerEmail: true
    });
    return Object.values(newErrors).every((e) => e === '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCaptchaError('');

    if (!validateAll()) return;

    if (!captchaToken) {
      setCaptchaChecked(false);
      setCaptchaError('Please complete the captcha checkbox.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        dob: formData.dateOfBirth || null,
        gender: formData.gender || null,
        email: formData.email || null,
        mobile: formData.mobile,
        referrer_name: formData.referrerName || null,
        referrer_email: formData.referrerEmail || null,
        notes: formData.notes || null,
        referral_source: referralSource,
        captcha_token: captchaToken
      };

      const resp = await fetch(`${apiBaseUrl}/api/personal-referrals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!resp.ok) {
        const errorBody = await resp.text();
        throw new Error(errorBody || 'submission failed');
      }

      setSubmitSuccess(true);
      resetCaptcha('');
    } catch (err) {
      console.error(err);
      if (String(err?.message || '').toLowerCase().includes('captcha')) {
        resetCaptcha('reCAPTCHA timed out. Please verify again.');
      }
      alert('There was a problem submitting the form');
      return;
    } finally {
      setIsSubmitting(false);
    }

    // setTimeout(() => {
    //   setShowReferralForm(false);
    //   setSubmitSuccess(false);
    //   setFormData(initialFormData);
    //   resetCaptcha('');
    // }, 2000);
  };

  // ── Shared render helpers ───────────────────────────────────────────────────
  const FieldError = ({ field }) =>
    errors[field] ? (
      <p className="text-red-500 text-xs mt-1" role="alert">{errors[field]}</p>
    ) : null;

  const inputClass = (field) =>
    `h-11 ${errors[field] ? 'border-red-500 focus:ring-red-500' : ''}`;

  // ── Static data ─────────────────────────────────────────────────────────────
  const steps = [
    {
      number: "01",
      title: "Submit Referral",
      icon: Send,
      description: "Online Form, Email, Phone, or Fax",
      details: [
        "Online Form",
        "Email: referrals@atlregen.com",
        "Phone: (404) 355-2728",
        "Fax: (404) 662-2344"
      ],
      whatWeNeed: [
        "Patient name and contact information",
        "Date of injury",
        "Case/matter number",
        "Brief description of injuries",
        "Your firm contact information"
      ]
    },
    {
      number: "02", 
      title: "We Handle Everything",
      icon: Calendar,
      description: "Patient Scheduling, Medical Records, Communication",
      details: [
        "Same-day or next-day appointments available",
        "6 convenient locations across GA and AL",
        "Flexible scheduling for injured patients"
      ],
      medicalRecords: [
        "Complete documentation for case files",
        "Timely reports within 5-7 business days",
        "Detailed treatment notes and progress reports"
      ],
      communication: [
        "Direct line to case coordinators",
        "Regular case status updates",
        "Available for attorney consultations"
      ]
    },
    {
      number: "03",
      title: "Patient Care & Follow-Up",
      icon: Users,
      description: "Comprehensive Treatment and Ongoing Support",
      details: [
        "Thorough evaluation and diagnosis",
        "Personalized treatment plans",
        "Regular progress updates",
        "Clear return-to-function goals"
      ],
      ongoingSupport: [
        "Case status available upon request",
        "Treatment summaries provided",
        "Coordination with other providers",
        "Return-to-work clearances when appropriate"
      ]
    }
  ];

  const whyChoose = [
    {
      icon: Award,
      title: "Medical Expertise",
      points: [
        "Board Certified physicians in Physical Medicine & Rehabilitation",
        "14+ years specializing in non-surgical orthopedic and regenerative medicine",
        "10,000+ successful treatments for personal injury patients",
        "Fellowship-trained at Hospital for Special Surgery"
      ]
    },
    {
      icon: Building,
      title: "Comprehensive Services",
      points: [
        "Regenerative Medicine: Stem cell therapy, PRP, tissue regeneration",
        "Interventional Spine: Epidurals, nerve blocks, spinal decompression",
        "Orthopedic Care: Joint injections, arthritis treatment, sports medicine",
        "Personal Injury Specialists: Auto accidents, workers compensation, slip & fall"
      ]
    },
    {
      icon: MapPin,
      title: "Convenience",
      points: [
        "6 locations: Riverdale, Smyrna, Decatur, Macon, LaGrange, Huntsville",
        "Same-day appointments for urgent cases",
        "Bilingual staff available",
        "Free parking at all locations"
      ]
    }
  ];

  const injuryTypes = [
    {
      icon: Car,
      title: "Auto Accidents",
      injuries: [
        "Whiplash and neck injuries",
        "Back pain and herniated discs",
        "Shoulder injuries",
        "Knee injuries",
        "Soft tissue injuries",
        "Concussion and post-concussion symptoms"
      ]
    },
    {
      icon: Briefcase,
      title: "Workers Compensation",
      injuries: [
        "Back injuries",
        "Repetitive stress injuries",
        "Joint injuries",
        "Neck and spine injuries",
        "Shoulder and rotator cuff injuries",
        "Post-surgical rehabilitation"
      ]
    }
  ];

  const faqs = [
    {
      q: "How quickly can you see new patients?",
      a: "We offer same-day or next-day appointments for urgent injury cases."
    },
    {
      q: "What documentation do you provide?",
      a: "Comprehensive medical reports, treatment notes, and progress updates for case files."
    },
    {
      q: "Do you work with personal injury cases?",
      a: "Yes, we specialize in treating auto accident and workers compensation injuries."
    },
    {
      q: "What if my client needs surgery?",
      a: "We specialize in non-surgical treatments. If surgery is indicated, we refer to trusted orthopedic surgeons and continue post-surgical rehabilitation."
    },
    {
      q: "Do you provide impairment ratings?",
      a: "Yes, our physicians can provide impairment ratings when clinically indicated."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B1B3A] via-[#2D2D4A] to-[#1B1B3A] py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#E63946] rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#20B2AA] rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 text-white px-5 py-2 rounded-full text-sm font-semibold mb-6">
                <Scale className="h-4 w-4" />
                <span>Attorney Referral Portal</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Partner with Atlanta's Leading <span className="text-[#E63946]">Personal Injury</span> Specialists
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Fast patient scheduling, comprehensive documentation, and expert non-surgical care for your clients. We make your job easier.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="bg-[#E63946] hover:bg-[#D32F3D] text-white px-8 py-6 text-lg font-bold"
                  onClick={() => setShowReferralForm(true)}
                  data-testid="submit-referral-btn"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Submit a Referral
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1B1B3A] px-8 py-6 text-lg font-bold" asChild>
                  <a href="tel:+14043552728">
                    <Phone className="mr-2 h-5 w-5" />
                    (404) 355-2728
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-8 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#E63946]">Same-Day</div>
                <div className="text-sm text-gray-600">Appointments Available</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#E63946]">5-7 Days</div>
                <div className="text-sm text-gray-600">Report Turnaround</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#E63946]">6</div>
                <div className="text-sm text-gray-600">Convenient Locations</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#E63946]">14+</div>
                <div className="text-sm text-gray-600">Years Experience</div>
              </div>
            </div>
          </div>
        </section>

        {/* 3-Step Process */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Quick Referral Process
              </h2>
              <p className="text-xl text-gray-600">Three simple steps to get your client the care they need</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {steps.map((step, index) => (
                <Card key={index} className="relative border-none shadow-xl bg-white hover:shadow-2xl transition-shadow">
                  <CardContent className="p-8">
                    <div className="absolute -top-4 left-8 bg-[#E63946] text-white text-sm font-bold px-4 py-1 rounded-full">
                      Step {step.number}
                    </div>
                    <div className="bg-gradient-to-br from-[#20B2AA]/10 to-[#E63946]/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mt-4">
                      <step.icon className="h-8 w-8 text-[#1B1B3A]" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600 mb-4">{step.description}</p>
                    <ul className="space-y-2">
                      {step.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Start Referral CTA */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto rounded-2xl bg-gradient-to-r from-[#1B1B3A] via-[#2D2D4A] to-[#3B2D5A] p-6 md:p-8 shadow-2xl">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#E63946]/20 px-4 py-1.5 text-xs font-semibold text-[#FF6B76] mb-3">
                    <Send className="h-3.5 w-3.5" />
                    <span>Quick & Easy Online Referral</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">Ready to refer a patient?</h3>
                  <p className="text-gray-300 text-sm md:text-base">
                    Submit your referral online in minutes. Same-day appointments available — we'll handle the rest.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <Button
                    className="bg-[#E63946] hover:bg-[#D32F3D] text-white font-bold rounded-full px-8"
                    onClick={() => setShowReferralForm(true)}
                    data-testid="start-referral-cta-btn"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Start Referral
                  </Button>
                  <a
                    href="tel:+14043552728"
                    className="inline-flex items-center gap-2 text-gray-200 hover:text-white transition-colors text-sm"
                  >
                    <Phone className="h-4 w-4 text-[#20B2AA]" />
                    Or call (404) 355-2728
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Attorneys Choose Us */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Why Attorneys Choose ATLREGEN
              </h2>
              <p className="text-xl text-gray-600">Trusted by law firms across Georgia and Alabama</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {whyChoose.map((item, index) => (
                <Card key={index} className="border-gray-200 hover:border-[#E63946] transition-colors">
                  <CardContent className="p-8">
                    <div className="bg-gradient-to-br from-[#E63946] to-[#D32F3D] w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <item.icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                    <ul className="space-y-3">
                      {item.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-600">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-1" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Injury Types */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Common Injury Types We Treat
              </h2>
              <p className="text-xl text-gray-600">Expert care for personal injury and workers compensation cases</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {injuryTypes.map((type, index) => (
                <Card key={index} className="border-none shadow-xl bg-white">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="bg-gradient-to-br from-[#1B1B3A] to-[#2D2D4A] w-14 h-14 rounded-xl flex items-center justify-center">
                        <type.icon className="h-7 w-7 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{type.title}</h3>
                    </div>
                    <ul className="space-y-3">
                      {type.injuries.map((injury, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-gray-600">
                          <ArrowRight className="h-4 w-4 text-[#E63946]" />
                          {injury}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Card */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="border-none shadow-2xl bg-gradient-to-br from-[#1B1B3A] to-[#2D2D4A] text-white overflow-hidden">
                <CardContent className="p-10 md:p-12">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div>
                      <h2 className="text-3xl font-bold mb-4">Referral Coordinator</h2>
                      <p className="text-gray-300 mb-6">
                        Our dedicated team is ready to assist with your referrals and provide case status updates.
                      </p>
                      <div className="space-y-4">
                        <a href="tel:+14043552728" className="flex items-center gap-3 text-lg hover:text-[#20B2AA] transition-colors">
                          <Phone className="h-5 w-5 text-[#E63946]" />
                          <span className="font-semibold">(404) 355-2728</span>
                        </a>
                        <a href="mailto:referrals@atlregen.com" className="flex items-center gap-3 hover:text-[#20B2AA] transition-colors">
                          <Mail className="h-5 w-5 text-[#E63946]" />
                          referrals@atlregen.com
                        </a>
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-[#E63946]" />
                          Fax: (404) 662-2344
                        </div>
                        <div className="flex items-center gap-3">
                          <Clock className="h-5 w-5 text-[#E63946]" />
                          Mon-Fri, 8:00 AM - 5:00 PM EST
                        </div>
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                      <h3 className="font-bold text-lg mb-4">What We Need for Referrals:</h3>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Patient name and contact information
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Date of injury
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Case/matter number
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Brief description of injuries
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-[#20B2AA] flex-shrink-0 mt-0.5" />
                          Your firm contact information
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <MessageSquare className="h-6 w-6 text-[#E63946] flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                        <p className="text-gray-600">{faq.a}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Case Status Tracking Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-[#E63946]/10 text-[#E63946] px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <ClipboardList className="h-4 w-4" />
                <span>Real-Time Updates</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Case Status <span className="text-[#E63946]">Tracking</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Stay informed every step of the way. Track your client's treatment progress with our transparent reporting system.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {[
                {
                  icon: Search,
                  title: "Case Lookup",
                  description: "Quick access to patient records and treatment history by case number"
                },
                {
                  icon: Bell,
                  title: "Automated Alerts",
                  description: "Email notifications for appointment completions and report availability"
                },
                {
                  icon: BarChart3,
                  title: "Progress Reports",
                  description: "Detailed treatment progress and outcome tracking for each patient"
                },
                {
                  icon: FileText,
                  title: "Document Portal",
                  description: "Secure access to medical records, reports, and billing statements"
                }
              ].map((feature, index) => (
                <Card key={index} className="border-gray-200 hover:border-[#E63946] hover:shadow-lg transition-all group">
                  <CardContent className="p-6 text-center">
                    <div className="bg-[#E63946]/10 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#E63946] transition-colors">
                      <feature.icon className="h-7 w-7 text-[#E63946] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Status Timeline Example */}
            <div className="max-w-4xl mx-auto">
              <Card className="border-none shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-[#1B1B3A] to-[#2D2D4A] p-6">
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    <ClipboardList className="h-5 w-5 text-[#E63946]" />
                    Sample Case Timeline
                  </h3>
                </div>
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {[
                      { status: "Referral Received", date: "Day 1", description: "Patient information received and case opened", complete: true },
                      { status: "Initial Appointment", date: "Day 1-2", description: "Patient scheduled for same-day or next-day evaluation", complete: true },
                      { status: "Evaluation Complete", date: "Day 2", description: "Comprehensive examination and diagnosis", complete: true },
                      { status: "Treatment Plan Created", date: "Day 3", description: "Personalized treatment protocol established", complete: true },
                      { status: "Progress Report Sent", date: "Day 5-7", description: "Detailed medical report delivered to your firm", complete: false, current: true },
                      { status: "Ongoing Treatment", date: "Ongoing", description: "Regular updates on patient progress and outcomes", complete: false }
                    ].map((step, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            step.complete ? 'bg-[#20B2AA]' : step.current ? 'bg-[#E63946]' : 'bg-gray-200'
                          }`}>
                            {step.complete ? (
                              <CheckCircle className="h-5 w-5 text-white" />
                            ) : step.current ? (
                              <Clock className="h-5 w-5 text-white" />
                            ) : (
                              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                            )}
                          </div>
                          {index < 5 && <div className={`w-0.5 h-full min-h-[40px] ${step.complete ? 'bg-[#20B2AA]' : 'bg-gray-200'}`}></div>}
                        </div>
                        <div className="pb-6">
                          <div className="flex items-center gap-3 mb-1">
                            <h4 className={`font-bold ${step.current ? 'text-[#E63946]' : 'text-gray-900'}`}>{step.status}</h4>
                            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{step.date}</span>
                          </div>
                          <p className="text-gray-600 text-sm">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-600 mb-6">
                Need a status update on an existing case? Contact our referral coordinator directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-[#E63946] hover:bg-[#D32F3D] text-white font-semibold px-8" asChild>
                  <a href="tel:+14043552728">
                    <Phone className="mr-2 h-5 w-5" />
                    Call (404) 355-2728
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white font-semibold px-8" asChild>
                  <a href="mailto:referrals@atlregen.com">
                    <Mail className="mr-2 h-5 w-5" />
                    Email for Updates
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Resources Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Downloadable Resources
              </h2>
            </div>

            <div className="flex flex-wrap justify-center gap-4 max-w-2xl mx-auto">
              <Button variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                <Download className="mr-2 h-4 w-4" />
                Referral Form PDF
              </Button>
              <Button variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                <Download className="mr-2 h-4 w-4" />
                Medical Records Request
              </Button>
              <Button variant="outline" className="border-2 border-[#1B1B3A] text-[#1B1B3A] hover:bg-[#1B1B3A] hover:text-white">
                <Download className="mr-2 h-4 w-4" />
                Case Status Inquiry
              </Button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-[#E63946]">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Refer a Patient?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Call us now or submit a referral online
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-[#E63946] hover:bg-gray-100 px-8 py-6 text-lg font-bold" asChild>
                <a href="tel:+14043552728">
                  <Phone className="mr-2 h-5 w-5" />
                  (404) 355-2728
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#E63946] px-8 py-6 text-lg font-bold" asChild>
                <a href="mailto:referrals@atlregen.com">
                  <Mail className="mr-2 h-5 w-5" />
                  referrals@atlregen.com
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Referral Form Modal */}
      {showReferralForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-4xl shadow-2xl my-4">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#1B1B3A] to-[#2D2D4A] px-6 py-4 rounded-t-xl sm:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-[#E63946] p-2 rounded-lg">
                    <Send className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Submit a Referral</h2>
                </div>
                <button
                  onClick={() => setShowReferralForm(false)}
                  className="text-white/70 hover:text-white transition-colors p-1"
                  data-testid="close-referral-modal"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Form Content */}
            {submitSuccess ? (
              <div className="p-8 sm:p-12 text-center">
                <div className="bg-green-100 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 sm:h-8 sm:w-8 text-green-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Referral Submitted!</h3>
                <p className="text-gray-600 text-sm">Our team will contact the patient within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} onFocus={() => setCaptchaReady(true)} className="p-6" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                  {/* First Name */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('firstName')}
                      aria-invalid={!!errors.firstName}
                      data-testid="first-name-input"
                    />
                    <FieldError field="firstName" />
                  </div>

                  {/* Last Name */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('lastName')}
                      aria-invalid={!!errors.lastName}
                      data-testid="last-name-input"
                    />
                    <FieldError field="lastName" />
                  </div>

                  {/* Date of Birth */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Date of Birth <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('dateOfBirth')}
                      aria-invalid={!!errors.dateOfBirth}
                      data-testid="dob-input"
                    />
                    <FieldError field="dateOfBirth" />
                  </div>

                  {/* Gender */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Gender</Label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full h-11 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent"
                      data-testid="gender-select"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  {/* Email */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Email</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('email')}
                      aria-invalid={!!errors.email}
                      data-testid="email-input"
                    />
                    <FieldError field="email" />
                  </div>

                  {/* Mobile */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Mobile <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('mobile')}
                      aria-invalid={!!errors.mobile}
                      data-testid="mobile-input"
                    />
                    <FieldError field="mobile" />
                  </div>

                  {/* Referrer Name */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">
                      Referrer Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      name="referrerName"
                      value={formData.referrerName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('referrerName')}
                      aria-invalid={!!errors.referrerName}
                      data-testid="referrer-name-input"
                    />
                    <FieldError field="referrerName" />
                  </div>

                  {/* Referrer Email */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Referrer Email</Label>
                    <Input
                      name="referrerEmail"
                      type="email"
                      value={formData.referrerEmail}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={inputClass('referrerEmail')}
                      aria-invalid={!!errors.referrerEmail}
                      data-testid="referrer-email-input"
                    />
                    <FieldError field="referrerEmail" />
                  </div>

                  {/* Notes */}
                  <div className="md:col-span-2 lg:col-span-3">
                    <Label className="text-gray-700 text-sm font-medium mb-2 block">Notes</Label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent resize-y"
                      data-testid="notes-input"
                    />
                  </div>
                </div>

                {/* reCAPTCHA — lazy: only mounts after first field focus */}
                <div className="mt-4">
                  <Label className="text-gray-700 text-sm font-medium mb-2 block">
                    Security Check <span className="text-red-500">*</span>
                  </Label>
                  {captchaReady && siteKey ? (
                    <ReCAPTCHA
                      key={captchaRenderKey}
                      ref={recaptchaRef}
                      sitekey={siteKey}
                      onChange={(token) => {
                        setCaptchaToken(token || '');
                        setCaptchaChecked(!!token);
                        setCaptchaError('');
                      }}
                      onExpired={() => resetCaptcha('reCAPTCHA timed out. Please verify again.')}
                      onErrored={() => resetCaptcha('reCAPTCHA timeout or network issue. Please try again.')}
                    />
                  ) : (
                    <div
                      className="flex items-center gap-3 border border-gray-300 rounded bg-gray-50 px-4 py-3 cursor-pointer text-sm text-gray-600"
                      onClick={() => setCaptchaReady(true)}
                    >
                      <span className="w-5 h-5 border-2 border-gray-400 rounded inline-block flex-shrink-0" />
                      I'm not a robot
                    </div>
                  )}
                  {captchaError && (
                    <p className="text-red-500 text-xs mt-2">{captchaError}</p>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-gray-200">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 px-6 bg-gray-600 text-white hover:bg-gray-700 border-none rounded-full"
                    onClick={() => setShowReferralForm(false)}
                    data-testid="cancel-form-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold h-11 px-8 rounded-full"
                    disabled={isSubmitting || !captchaChecked}
                    data-testid="submit-form-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ForAttorneys;